import { useAuth } from '../../contexts/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiError } from 'api/generated';
import clsx from 'clsx';
import { PasswordMeterComponent } from 'components/layout/initializers/password-meter-component';
import { useIdentityUsersPasswordResetCheckMutation } from 'features/auth/api/repositories/use-identity-users-password-reset-check.mutation';
import { useIdentityUsersPasswordResetSubmitMutation } from 'features/auth/api/repositories/use-identity-users-password-reset-submit.mutation';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

type FormValues = {
  code: string;
  password: string;
  changePassword: string;
};

const loginSchema = yup.object().shape({
  code: yup.string().min(4, 'Minimum 4 symbols').max(4, 'Maximum 4 symbols').required('Email code'),
  password: yup.string().min(8, 'Minimum 8 symbols').max(50, 'Maximum 50 symbols').required('Password is required'),
  changePassword: yup.string().when('password', {
    is: (value: string) => (value && value.length > 0 ? true : false),
    then: (schema) => schema.oneOf([yup.ref('password')], "Password and Confirm Password didn't match")
  })
});

const defaultValues: FormValues = {
  code: '',
  password: '',
  changePassword: ''
};

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const secret = searchParams.get('secret');
  const navigate = useNavigate();

  if (!code || !secret) {
    navigate('/error/404');
  }

  const { saveAuth } = useAuth();
  const [formStatus, setFormStatus] = useState('');
  const [score, setScore] = useState<number>(0);
  const { mutateAsync: useIdentityUsersPasswordResetCheck } = useIdentityUsersPasswordResetCheckMutation();
  const { mutateAsync: useIdentityUsersPasswordResetSubmit, isLoading: isSubmitting } =
    useIdentityUsersPasswordResetSubmitMutation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, isLoading: loading, errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(loginSchema),
    mode: 'onBlur'
  });

  useEffect(() => {
    useIdentityUsersPasswordResetCheck(
      {
        input: {
          userCode: code!,
          requestBody: secret!
        }
      },
      {
        onSuccess: () => {
          /**/
        },
        onError: (error) => {
          console.error(error);
          navigate('/error/404');
        }
      }
    );
  }, []);

  useEffect(() => {
    PasswordMeterComponent.bootstrap();
  });

  useEffect(() => {
    const element = document.body.querySelectorAll('[data-kt-password-meter]')[0] as HTMLElement;
    if (element) {
      const meter = PasswordMeterComponent.getInstance(element);
      setScore(meter?.getScore() ?? 0);
    }
  }, [watch('password')]);

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    useIdentityUsersPasswordResetSubmit(
      {
        input: {
          userCode: code!,
          requestBody: {
            code: values.code,
            secret: secret,
            newPassword: values.password
          }
        }
      },
      {
        onSuccess: () => {
          navigate('/auth/login');
        },
        onError: (error) => {
          saveAuth(undefined);
          let errorMessage = 'The login detail is incorrect';
          if (error instanceof ApiError) {
            if (error.body.error.message) errorMessage = error.body.error.message;
          }
          setFormStatus(errorMessage);
          console.error(error);
        }
      }
    );
  };

  const showPassword = (elName: string) => {
    const x = document.getElementsByName(elName)[0] as HTMLInputElement;
    if (x.type === 'password') {
      x.type = 'text';
    }
  };

  const hidePassword = (elName: string) => {
    const x = document.getElementsByName(elName)[0] as HTMLInputElement;
    if (x.type === 'text') {
      x.type = 'password';
    }
  };

  return (
    <form
      className="form w-100"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      id="kt_login_signin_form"
      autoComplete="off"
    >
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Reset Password</h1>
        <div className="fs-6 mt-6">
          <p>We have sent a 4 digit code on you email address.</p>
        </div>
      </div>
      {/* begin::Heading */}

      {formStatus && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formStatus}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Email Code</label>
        <input
          readOnly
          onFocus={() => {
            const codeInput = document.getElementsByName('code')[0];
            if (codeInput) {
              codeInput.removeAttribute('readOnly');
            }
          }}
          placeholder="Email Code"
          {...register('code')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': touchedFields.code && errors.code },
            {
              'is-valid': touchedFields.code && !errors.code
            }
          )}
          type="text"
          name="code"
          autoComplete="off"
        />
        {touchedFields.code && errors.code && (
          <div className="fv-plugins-message-container">
            <span role="alert">{errors.code?.message}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Password */}
      <div className="fv-row mb-8" data-kt-password-meter="true">
        <div className="mb-1">
          <label className="form-label fw-bolder text-dark fs-6">New Password</label>
          <div className="position-relative mb-3">
            <div className="input-group input-group-solid">
              <span className="input-group-text cursor-pointer">
                <i
                  className="bi bi-eye-fill fs-2"
                  onMouseDown={() => {
                    showPassword('password');
                  }}
                  onMouseUp={() => {
                    hidePassword('password');
                  }}
                  onMouseLeave={() => {
                    hidePassword('password');
                  }}
                >
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
              </span>
              <input
                type="password"
                placeholder="Password"
                autoComplete="off"
                {...register('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': touchedFields.password && (errors.password || score !== 100)
                  },
                  {
                    'is-valid': score === 100
                  }
                )}
              />
            </div>
            {touchedFields.password && errors.password && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <span role="alert" className="text-danger">
                    {errors.password.message}
                  </span>
                </div>
              </div>
            )}
          </div>
          {/* begin::Meter */}
          <div className="d-flex align-items-center mb-3" data-kt-password-meter-control="highlight">
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px"></div>
          </div>
          {/* end::Meter */}
        </div>
        <div className="text-muted">Use 8 or more characters with a mix of letters, numbers & symbols.</div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group Confirm password */}
      <div className="fv-row mb-5">
        <label className="form-label fw-bolder text-dark fs-6">Repeat New Password</label>
        <div className="input-group input-group-solid">
          <span className="input-group-text cursor-pointer">
            <i
              className="bi bi-eye-fill fs-2"
              onMouseDown={() => {
                showPassword('changePassword');
              }}
              onMouseUp={() => {
                hidePassword('changePassword');
              }}
              onMouseLeave={() => {
                hidePassword('changePassword');
              }}
            >
              <span className="path1"></span>
              <span className="path2"></span>
            </i>
          </span>
          <input
            type="password"
            placeholder="Password confirmation"
            autoComplete="off"
            {...register('changePassword')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': touchedFields.changePassword && errors.changePassword
              },
              {
                'is-valid': touchedFields.changePassword && !errors.changePassword
              }
            )}
          />
        </div>
        {touchedFields.changePassword && errors.changePassword && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert" className="text-danger">
                {errors.changePassword.message}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={isSubmitting || !isValid || score !== 100}
        >
          {!loading && !isSubmitting && <span className="indicator-label">Continue</span>}
          {(loading || isSubmitting) && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}
